import { Fragment, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * O tooltip dos gráficos (`#tip` no protótipo).
 *
 * É um só, posicionado no cursor, e não um por elemento. A razão é
 * concreta: o mapa de calor tem 7 × 6 células e as barras dezenas de
 * segmentos — um componente de tooltip por elemento significaria
 * centenas de nós ociosos no DOM e outros tantos listeners.
 *
 * Não é o Radix Tooltip de propósito. O do Radix é ancorado no
 * elemento e pensado para foco de teclado, que é o certo para um
 * botão de ícone; este segue o CURSOR sobre uma área de gráfico, que
 * é comportamento diferente. Os dois convivem — `DicaDeFoco` abaixo é
 * o do Radix, para os ícones da topbar.
 */

interface ConteudoDaDica {
  readonly titulo: string;
  /**
   * Aceita <br> e <em>, como no protótipo — e por isso SÓ para texto
   * que é constante da própria tela (os gráficos). Dado digitado por
   * alguém nunca passa por aqui.
   */
  readonly texto?: string;
  /**
   * Texto PURO, uma linha por item: o caminho para qualquer dado de
   * gente — o nome de quem reservou a quadra, o título de um evento.
   * Vira nó de texto do React, não HTML.
   */
  readonly linhas?: readonly string[];
  /** A linha em destaque (o <em> do protótipo), também texto puro. */
  readonly destaque?: string;
}

interface ControleDaDica {
  mostrar: (evento: { clientX: number; clientY: number }, conteudo: ConteudoDaDica) => void;
  mover: (evento: { clientX: number; clientY: number }) => void;
  esconder: () => void;
}

const Contexto = createContext<ControleDaDica | undefined>(undefined);

export function useDica(): ControleDaDica {
  const controle = useContext(Contexto);
  if (!controle) throw new Error('useDica precisa de <ProvedorDeDica> acima na árvore.');
  return controle;
}

/**
 * Um atalho para os gráficos: devolve os handlers já prontos para
 * espalhar num elemento SVG ou div.
 */
export function useDicaEm(conteudo: ConteudoDaDica) {
  const dica = useDica();
  return useMemo(
    () => ({
      onMouseEnter: (e: { clientX: number; clientY: number }) => dica.mostrar(e, conteudo),
      onMouseMove: (e: { clientX: number; clientY: number }) => dica.mover(e),
      onMouseLeave: () => dica.esconder(),
    }),
    // As linhas entram na dependência como texto: um array novo a cada
    // render refaria os handlers à toa.
    [dica, conteudo.titulo, conteudo.texto, conteudo.linhas?.join('\n'), conteudo.destaque],
  );
}

export function ProvedorDeDica({ children }: { children: ReactNode }) {
  const [conteudo, setConteudo] = useState<ConteudoDaDica | undefined>();
  const elemento = useRef<HTMLDivElement>(null);

  const posicionar = useCallback((e: { clientX: number; clientY: number }) => {
    const no = elemento.current;
    if (!no) return;
    no.style.left = `${e.clientX}px`;
    no.style.top = `${e.clientY - 10}px`;
  }, []);

  const controle = useMemo<ControleDaDica>(
    () => ({
      mostrar: (e, c) => {
        setConteudo(c);
        posicionar(e);
      },
      mover: posicionar,
      esconder: () => setConteudo(undefined),
    }),
    [posicionar],
  );

  return (
    <Contexto.Provider value={controle}>
      {children}
      <div id="tip" ref={elemento} className={conteudo ? 'on' : undefined} role="tooltip">
        {conteudo && (
          <>
            <b>{conteudo.titulo}</b>
            {conteudo.linhas ? (
              // Dado de gente: nó de texto, nunca HTML. O nome "Breno
              // <img onerror=…>" aparece como foi digitado, e não roda.
              <small>
                {conteudo.linhas.map((linha, i) => (
                  <Fragment key={i}>
                    {i > 0 && <br />}
                    {linha}
                  </Fragment>
                ))}
                {conteudo.destaque && (
                  <>
                    <br />
                    <em>{conteudo.destaque}</em>
                  </>
                )}
              </small>
            ) : (
              // `texto` é constante de tela (os gráficos), nunca dado do
              // usuário — por isso o HTML embutido com <br> e <em> é
              // seguro aqui. Nome de aluno ou de cliente vai em `linhas`.
              <small dangerouslySetInnerHTML={{ __html: conteudo.texto ?? '' }} />
            )}
          </>
        )}
      </div>
    </Contexto.Provider>
  );
}
